<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { requestMagicLink } from '../api';
import { loadRecaptchaScript, executeRecaptcha } from '../services/recaptcha';
import { isValidEmail } from '../services/validators';

const instance = getCurrentInstance();
const route = instance?.proxy?.$route;

const email = ref('');
const emailError = ref('');
const loading = ref(false);
const success = ref(false);

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: '/users/sign_in', text: 'Back to Login' }];
  }
  return [
    { url: '/users/sign_in', text: 'Sign In' },
    { url: '/users/password/new', text: 'Forgot your password?' },
  ];
});

const bottomLinks = computed(() => {
  if (success.value) return [];
  return [{ url: '/users/request', text: 'Not a member yet? Request access' }];
});

async function handleSubmit() {
  emailError.value = '';

  if (!isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    return;
  }

  loading.value = true;

  try {
    const token = await executeRecaptcha('MagicLinkRequest');
    if (!token) {
      emailError.value = 'Security verification failed. Please refresh and try again.';
      return;
    }

    await requestMagicLink(email.value, token);
    success.value = true;
  } catch {
    emailError.value = 'Unable to send sign-in link. Please try again.';
  } finally {
    loading.value = false;
  }
}

function handleResend() {
  success.value = false;
  handleSubmit();
}

onMounted(async () => {
  const queryEmail = route?.query.email;
  if (typeof queryEmail === 'string') {
    email.value = queryEmail;
  }

  try {
    await loadRecaptchaScript();

    if (email.value && route?.query.auto_submit) {
      handleSubmit();
    }
  } catch (error) {
    console.error('reCAPTCHA failed to load:', error);
  }
});
</script>

<template>
  <AuthLayout title="Sign in with email" :top-links="topLinks" :bottom-links="bottomLinks">
    <div v-if="success" class="text-white text-left">
      <p class="mb-2">We've sent a sign-in link to your email. Click it to access your account.</p>
      <p class="text-sm text-white/75 mb-3">The link expires in 10 minutes.</p>
      <div class="pt-2 border-t border-white/20 text-white/80">
        <span class="text-sm mr-1">Didn't receive it?</span>
        <a href="#" class="text-sw-purple font-medium no-underline hover:underline" @click.prevent="handleResend">
          Resend
        </a>
      </div>
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <p class="text-white/60 text-sm mb-4">The sign-in link will expire in 10 minutes.</p>

      <Input v-model="email" type="email" placeholder="Email" autocomplete="email" :error="emailError" />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading">
          Send sign-in link
        </Button>
      </div>
    </form>
  </AuthLayout>
</template>
